import { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { useParams } from "react-router-dom";
import { IParams } from "@models/IParams";
import { Classroom, CubeQuery } from "@models/ISchema";
import { classroomQuery } from "../graphql/classroom.query";
import { useDebounce } from "@common/hooks/useDebounce";

export interface ClassroomOption extends Classroom {
  label: string;
  value: string;
  isDisabled: boolean;
}

const serializeOptions = (classrooms: Classroom[]): ClassroomOption[] => {
  if (!classrooms?.length) return [];

  return classrooms.map(classroom => ({
    ...classroom,
    label: `${classroom.code} - ${classroom.name} - ${classroom?.building?.campus?.code || ""}`,
    value: classroom.id,
    isDisabled: !classroom?.usable,
  }));
};

export const useClassroomSearch = (enabled = true) => {
  const client = useApolloClient();
  const { scenario, origin }: IParams = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<ClassroomOption[]>([]);

  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (!enabled) setSearchTerm("");
    if (!enabled || !searchTerm || debouncedSearch !== searchTerm) {
      setOptions([]);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    const fetchOptions = async () => {
      try {
        const { data } = await client.query({
          query: classroomQuery,
          variables: {
            originId: origin,
            scenarioId: scenario,
            filter: {
              page: 1,
              size: 20,
              searchTerm: debouncedSearch,
            },
          },
        });
        const dataQuery: CubeQuery = data?.cube;
        if (active) setOptions(serializeOptions(dataQuery?.classrooms?.items));
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    void fetchOptions();
    return () => {
      active = false;
    };
  }, [client, enabled, scenario, origin, searchTerm, debouncedSearch]);

  return {
    options,
    loading,
    onSearch: setSearchTerm,
  };
};

export default useClassroomSearch;
